import React, { useState, useRef, useEffect } from 'react';
import './ChatWidget.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FALLBACK_ERROR_MESSAGE =
  "Sorry, I'm having trouble connecting right now. Please try again in a moment, or reach us directly at solutions@googlyn.ca.";

const SESSION_STORAGE_KEY = 'gl_chat_session_id';

const formatNow = () => {
  const d = new Date();
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
};

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!sid) {
    sid =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
  }
  return sid;
};

export const ChatWidget = ({ onOpenDemoModal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(null);

  if (!sessionIdRef.current) {
    sessionIdRef.current = getSessionId();
  }

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [isOpen, messages, isTyping]);

  const callChatApi = async (message) => {
    const res = await fetch(`${BACKEND_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, session_id: sessionIdRef.current }),
    });
    if (!res.ok) throw new Error('Chat request failed');
    return res.json();
  };

  const requestBotReply = async (message) => {
    setIsTyping(true);
    try {
      const data = await callChatApi(message);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          role: 'bot',
          text: data.response,
          time: formatNow(),
          intent: data.intent,
        },
      ]);
      setQuickReplies(data.quick_replies || []);

      if (data.intent === 'book_demo' && onOpenDemoModal) {
        setTimeout(() => onOpenDemoModal(), 600);
      }
    } catch (err) {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-error-${Date.now()}`,
          role: 'bot',
          text: FALLBACK_ERROR_MESSAGE,
          time: formatNow(),
        },
      ]);
      setQuickReplies([]);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTeaserDismissed(true);
    if (!hasStarted) {
      setHasStarted(true);
      requestBotReply('greeting');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleRestart = () => {
    setMessages([]);
    setQuickReplies([]);
    setInput('');
    setIsTyping(false);
    setHasStarted(true);
    requestBotReply('greeting');
  };

  const sendUserMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: trimmed, time: formatNow() },
    ]);
    setQuickReplies([]);
    requestBotReply(trimmed);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    sendUserMessage(input);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (text) => {
    sendUserMessage(text);
  };

  return (
    <div className="gl-chat-root" data-testid="chat-widget-root">
      {!isOpen && !teaserDismissed && (
        <div className="gl-chat-teaser" data-testid="chat-teaser">
          <p className="gl-chat-teaser-title">
            Questions about what we build? Ask the concierge.
          </p>
          <p className="gl-chat-teaser-subtitle">Replies instantly</p>
          <button
            type="button"
            className="gl-chat-teaser-close"
            aria-label="Dismiss"
            onClick={() => setTeaserDismissed(true)}
            data-testid="chat-teaser-close"
          >
            ×
          </button>
        </div>
      )}

      {isOpen && (
        <div className="gl-chat-panel" data-testid="chat-panel" role="dialog" aria-label="GoogLyn Concierge Chat">
          <div className="gl-chat-header">
            <div className="gl-chat-header-avatar">GL</div>
            <div className="gl-chat-header-text">
              <div className="gl-chat-header-name">GoogLyn Concierge</div>
              <div className="gl-chat-header-status">
                <span className="gl-chat-status-dot" />
                <span>GoogLyn AI · Online</span>
              </div>
            </div>
            <div className="gl-chat-header-actions">
              <button
                type="button"
                className="gl-chat-icon-btn"
                aria-label="Restart conversation"
                onClick={handleRestart}
                data-testid="chat-restart-btn"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M3 12a9 9 0 1 0 3-6.7" />
                  <polyline points="3 4 3 10 9 10" />
                </svg>
              </button>
              <button
                type="button"
                className="gl-chat-icon-btn"
                aria-label="Minimise"
                onClick={handleClose}
                data-testid="chat-minimise-btn"
              >
                <svg viewBox="0 0 24 24">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="gl-chat-messages" ref={messagesEndRef} data-testid="chat-messages">
            <div className="gl-chat-divider">
              <span className="gl-chat-divider-text">TODAY</span>
            </div>

            {messages.map((msg) =>
              msg.role === 'bot' ? (
                <div className="gl-chat-row-bot" key={msg.id}>
                  <div className="gl-chat-bot-avatar">GL</div>
                  <div className="gl-chat-bubble-group">
                    <div className="gl-chat-bubble-bot">
                      <div>{msg.text}</div>
                      {msg.intent === 'demos' && (
                        <a
                          href="/demos"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gl-chat-inline-link"
                          data-testid="chat-demos-link"
                        >
                          View live demos ↗
                        </a>
                      )}
                    </div>
                    <div className="gl-chat-timestamp">{msg.time}</div>
                  </div>
                </div>
              ) : (
                <div className="gl-chat-row-user" key={msg.id}>
                  <div className="gl-chat-bubble-user">{msg.text}</div>
                </div>
              )
            )}

            {isTyping && (
              <div className="gl-chat-row-bot" data-testid="chat-typing-indicator">
                <div className="gl-chat-bot-avatar">GL</div>
                <div className="gl-chat-bubble-group">
                  <div className="gl-chat-bubble-bot gl-chat-typing">
                    <span className="gl-chat-typing-dot" />
                    <span className="gl-chat-typing-dot" />
                    <span className="gl-chat-typing-dot" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {quickReplies.length > 0 && (
            <div className="gl-chat-quick-replies" data-testid="chat-quick-replies">
              {quickReplies.map((qr) => (
                <button
                  type="button"
                  key={qr}
                  className="gl-chat-chip"
                  onClick={() => handleQuickReply(qr)}
                  data-testid={`chat-quick-reply-${qr.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {qr}
                </button>
              ))}
            </div>
          )}

          <div className="gl-chat-input-row">
            <input
              type="text"
              className="gl-chat-input"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="chat-input"
            />
            <button
              type="button"
              className="gl-chat-send"
              aria-label="Send message"
              onClick={handleSend}
              data-testid="chat-send-btn"
            >
              <svg viewBox="0 0 24 24">
                <path d="M4 20L20 12 4 4 4 10 15 12 4 14 Z" />
              </svg>
            </button>
          </div>

          <div className="gl-chat-footer">
            Powered by <span className="gl-chat-footer-brand">GoogLyn AI</span>
          </div>
        </div>
      )}

      {!isOpen && (
        <button
          type="button"
          className="gl-chat-launcher"
          aria-label="Open chat"
          onClick={handleOpen}
          data-testid="chat-launcher"
        >
          GL
        </button>
      )}
    </div>
  );
};

export default ChatWidget;
